import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { ILegalRequirementTemplateFullObject } from '../Interfaces/ILegalRequirementTemplate';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { useSelector } from 'react-redux';
import { State } from '../redux/mainReducer';
import { TextModuleSelector } from '../TextModule/TextModuleSelector';
import { CustomEditor } from '../core/CustomEditor';
import { ICourtCourtRole } from '../Interfaces/IAemter';
import { useFetch } from '../hooks/useFetch';
import { Edit } from '@mui/icons-material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { ILegalPhaseTemplateFullObject } from '../Interfaces/ILegalPhaseTemplate';
import { ILegalRequirementTemplateDeadline } from '../Interfaces/ILegalRequirementTemplate_deadlines';
import { AddPrerequisiteDeadline } from './AddPrerequisiteDeadline';
import { LegalRequirementDeadlineTable } from './LegalRequirementDeadlineTable';
import { DocumentOverview } from '../Document/DocumentOverview';
import { IDocument } from '../Interfaces/IDocument';
import { CustomeTextField } from '../generic/CustomeTextField';
import { RequirementResponsibilityCard } from '../LegalTransaction/LegalPhase/Edit/RequirementResponsibilityCard';
import { CollapsibleSection } from '../core/CollapsibleSection';
import { SectionNavLayout } from '../core/SectionNavLayout';
import { TaskAutomationManager } from './TaskAutomationManager';
import { KnowledgeItemManager } from './KnowledgeItemManager';

interface IProps {
  currentRequirementTemplate: ILegalRequirementTemplateFullObject;
  setCurrentRequirementTemplate: Function;
  legalRequirementTemplates: ILegalRequirementTemplateFullObject[];
  legalPhaseTemplateArray?: ILegalPhaseTemplateFullObject[];
}

export const EditLegalRequirementTemplateData: React.FC<IProps> = ({
  currentRequirementTemplate,
  setCurrentRequirementTemplate,
  legalRequirementTemplates,
  legalPhaseTemplateArray,
}) => {
  const isSendEmailPossible =
    currentRequirementTemplate.sendMail ||
    currentRequirementTemplate.isInterneLegalRequirement;

  useEffect(() => {
    const updatedArray = currentRequirementTemplate.DocumentArray
      ? currentRequirementTemplate.DocumentArray.map(doc => ({
          ...doc,
          perEmail: isSendEmailPossible ? doc.perEmail : false,
        }))
      : [];

    setCurrentDocumentArray(updatedArray);
  }, [isSendEmailPossible]);

  const legalRequirementStateArray = useSelector(
    (state: State) =>
      state.legalRequirementStateArray.storeLegalRequirementState
  );

  const [courtcourtRoleArray, , wasSuccessfullyLoadCourt] =
    useFetch<ICourtCourtRole[]>('/courtcourtrole');

  const [currentDocumentArray, setCurrentDocumentArray] = useState<IDocument[]>(
    currentRequirementTemplate.DocumentArray
  );

  useEffect(() => {
    setCurrentRequirementTemplate({
      ...currentRequirementTemplate,
      DocumentArray: currentDocumentArray,
    });
  }, [currentDocumentArray]);

  const [addPrerequisteDeadlineOpen, setAddPrerequisteDeadlineOpen] =
    useState(false);

  const handleChangePrerequisteDeadline = useCallback(
    (changedPrequiste: ILegalRequirementTemplateDeadline) => {
      const newArray = currentRequirementTemplate.DeadlineArray
        ? currentRequirementTemplate.DeadlineArray
        : [];

      if (changedPrequiste.idDeadline === -1) {
        changedPrequiste.idDeadline = Math.floor(Math.random() * 1000000);
        newArray.push(changedPrequiste);
      } else {
        const index = newArray.findIndex(
          x => x.idDeadline === changedPrequiste.idDeadline
        );
        if (index === -1) {
          newArray.push(changedPrequiste);
        } else {
          newArray[index] = changedPrequiste;
        }
      }
      setCurrentRequirementTemplate({
        ...currentRequirementTemplate,
        DeadlineArray: newArray,
      });
    },
    [currentRequirementTemplate]
  );

  const handleDeleteDeadline = useCallback(
    (index: number) => {
      const newArray = currentRequirementTemplate.DeadlineArray
        ? currentRequirementTemplate.DeadlineArray
        : [];
      newArray.splice(index, 1);
      setCurrentRequirementTemplate({
        ...currentRequirementTemplate,
        DeadlineArray: newArray,
      });
    },
    [currentRequirementTemplate]
  );

  if (!wasSuccessfullyLoadCourt || !wasSuccessfullyLoadCourt) {
    return <div>Fehler beim Laden der Daten</div>;
  } else if (courtcourtRoleArray === undefined) {
    return (
      <div>
        <CircularProgress></CircularProgress>
      </div>
    );
  }

  // === Kopfbereich (#1244): Grunddaten oben (wie im Vorgang) ===
  const headerContent = (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
      <Grid container spacing={2} alignItems='center'>
        <Grid item sm={2}>
          <TextField
            label='interne Maßnahme'
            value={
              currentRequirementTemplate.isInterneLegalRequirement
                ? 'true'
                : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                isInterneLegalRequirement: event.target.value === 'true',
                sendMail:
                  event.target.value === 'true'
                    ? false
                    : currentRequirementTemplate.sendMail,
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
        <Grid item sm={6}>
          <TextField
            label='Bedingung'
            value={currentRequirementTemplate.LegalRequirementTemplate}
            error={currentRequirementTemplate.LegalRequirementTemplate === ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                LegalRequirementTemplate: event.target.value,
              })
            }
            size='small'
            fullWidth
          />
        </Grid>
        <Grid item sm={2}>
          <TextField
            label='MA lädt Dateien hoch'
            value={
              currentRequirementTemplate.canUploadFilesEmployees
                ? 'true'
                : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                canUploadFilesEmployees: event.target.value === 'true',
                DocumentArray:
                  event.target.value === 'false'
                    ? []
                    : currentRequirementTemplate.DocumentArray,
              });
              setCurrentDocumentArray(
                event.target.value === 'false' ? [] : currentDocumentArray
              );
            }}
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
        <Grid item sm={2}>
          <TextField
            label='Mandant lädt Dateien hoch'
            value={
              currentRequirementTemplate.canUploadFilesClient ? 'true' : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirementTemplate({
                ...currentRequirementTemplate,
                canUploadFilesClient: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );

  // === Reiter-Inhalte (#1244) — Logik unverändert, nur gruppiert ===
  const allgemeinContent = (
    <Grid container spacing={2}>
      <Grid item sm={5}>
        <TextField
          label='Startstatus'
          value={currentRequirementTemplate.idLegalRequirementState}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              idLegalRequirementState: Number(event.target.value),
            })
          }
          size='small'
          fullWidth
          select
        >
          {legalRequirementStateArray.map(x => (
            <MenuItem
              key={`idLegalRequirementState-${x.idLegalRequirementState}`}
              value={x.idLegalRequirementState}
            >
              {x.LegalRequirementState}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item sm={3}>
        <TextField
          label='Vorlage im MA-Katalog'
          disabled={currentRequirementTemplate.idLegalPhaseTemplate === null}
          value={currentRequirementTemplate.isTemplate ? 'true' : 'false'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              isTemplate: event.target.value === 'true',
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value='true'>Ja</MenuItem>
          <MenuItem value='false'>Nein</MenuItem>
        </TextField>
      </Grid>

      <Grid item sm={4}>
        <TextField
          label='Vorgänger (Bedingung)'
          value={
            currentRequirementTemplate.Predecessor_idLegalRequirementTemplate ===
            undefined
              ? ''
              : currentRequirementTemplate.Predecessor_idLegalRequirementTemplate
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              Predecessor_idLegalRequirementTemplate: Number(
                event.target.value
              ),
            })
          }
          size='small'
          fullWidth
          select
        >
          {legalRequirementTemplates.map(x => (
            <MenuItem
              key={`predecessor-idLegalRequirementTemplate-${x.idLegalRequirementTemplate}`}
              value={x.idLegalRequirementTemplate}
            >
              {x.LegalRequirementTemplate}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item sm={12}>
        <TextModuleSelector
          key={`type-legalrequirementedit`}
          floatRight
          idTextModuleCategory={4}
          txt={currentRequirementTemplate.InfoText}
          setTxt={(txt: string) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              InfoText: txt,
            })
          }
        />
        <TextField
          label='Infotext'
          value={
            currentRequirementTemplate.InfoText === null
              ? ''
              : currentRequirementTemplate.InfoText
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              InfoText: event.target.value === '' ? null : event.target.value,
            })
          }
          size='small'
          fullWidth
          multiline
          rows={3}
        />
      </Grid>

      {/* Fehler-Konfiguration */}
      <Grid item sm={6}>
        <TextField
          label='Fehler möglich'
          value={currentRequirementTemplate.hasError ? 'true' : 'false'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              hasError: event.target.value === 'true',
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value='true'>Ja</MenuItem>
          <MenuItem value='false'>Nein</MenuItem>
        </TextField>
      </Grid>
      <Grid item sm={6}>
        <TextField
          label='Freitext bei Fehler erlauben'
          value={currentRequirementTemplate.hasErrorFreeText ? 'true' : 'false'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              hasErrorFreeText: event.target.value === 'true',
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value='true'>Ja</MenuItem>
          <MenuItem value='false'>Nein</MenuItem>
        </TextField>
      </Grid>
      <Grid item sm={12}>
        <TextField
          label='Fehlermeldung'
          value={currentRequirementTemplate.TextOfError}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              TextOfError: event.target.value,
            })
          }
          size='small'
          fullWidth
          multiline
          rows={3}
        />
      </Grid>
    </Grid>
  );

  const emailContent = (
    <Grid container spacing={2}>
      <Grid item sm={4}>
        <TextField
          label='Auto. E-Mail versenden (wenn erfüllt)'
          value={currentRequirementTemplate.sendMail ? 'true' : 'false'}
          disabled={currentRequirementTemplate.isInterneLegalRequirement}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              sendMail: event.target.value === 'true',
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value='true'>Ja</MenuItem>
          <MenuItem value='false'>Nein</MenuItem>
        </TextField>
      </Grid>

      <Grid item sm={8}>
        <CustomeTextField
          label='E-Mail Betreff'
          attr='EmailSubject'
          object={currentRequirementTemplate}
          setObject={setCurrentRequirementTemplate}
          type='string'
          size='small'
          maxLength='md'
          error={
            currentRequirementTemplate.sendMail &&
            (currentRequirementTemplate.EmailSubject === '' ||
              currentRequirementTemplate.EmailSubject === null)
          }
          helperText={
            currentRequirementTemplate.sendMail &&
            (currentRequirementTemplate.EmailSubject === '' ||
              currentRequirementTemplate.EmailSubject === null)
              ? 'E-Mail Betreff ist erforderlich'
              : undefined
          }
        />
      </Grid>

      <Grid item sm={12}>
        <CustomEditor
          txt={
            currentRequirementTemplate.EmailBody === null
              ? ''
              : currentRequirementTemplate.EmailBody
          }
          setTxt={(txt: string) =>
            setCurrentRequirementTemplate({
              ...currentRequirementTemplate,
              EmailBody: txt === '' ? null : txt,
            })
          }
          idTextModuleCategory={3}
          dictonaryIdsArray={[1, 2, 3]}
        />
      </Grid>
    </Grid>
  );

  const zustaendigkeitContent = (
    <RequirementResponsibilityCard
      mode='template'
      value={currentRequirementTemplate}
      onChange={next =>
        setCurrentRequirementTemplate({
          ...currentRequirementTemplate,
          ...next,
        })
      }
    />
  );

  const bereitgestellteDatenContent =
    currentRequirementTemplate.canUploadFilesEmployees ? (
      <DocumentOverview
        documentArray={currentDocumentArray}
        setDocumentArray={setCurrentDocumentArray}
        allowUpload={true}
        allowRemove={true}
        title={'Standarddokumente'}
        isStandarddocument={true}
        isTemplate
        isSendEmailPossible={isSendEmailPossible}
      />
    ) : (
      <Alert severity='info'>
        Aktivieren Sie im Reiter „Allgemein" die Option „Mitarbeiter kann
        Dateien hochladen", um Standarddokumente (bereitgestellte Daten) zu
        hinterlegen.
      </Alert>
    );

  const startbedingungenContent = (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <CollapsibleSection
          title='Startbedingungen'
          subtitle={
            currentRequirementTemplate.DeadlineArray &&
            currentRequirementTemplate.DeadlineArray.length > 0
              ? `${currentRequirementTemplate.DeadlineArray.length} Bedingung(en) hinterlegt`
              : 'Wann diese Maßnahme starten darf — optional'
          }
          icon={<PlaylistAddCheckIcon color='action' />}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} display='flex' justifyContent='flex-end'>
              <Tooltip title='Bearbeiten Sie die Startbedingungen'>
                <IconButton
                  size='small'
                  onClick={() => setAddPrerequisteDeadlineOpen(true)}
                >
                  <Edit fontSize='small' />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <LegalRequirementDeadlineTable
                deadlineArray={currentRequirementTemplate.DeadlineArray}
                setDeadline={handleChangePrerequisteDeadline}
                deleteDeadline={handleDeleteDeadline}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>
    </Grid>
  );

  const aufgabenContent = (
    <TaskAutomationManager
      items={currentRequirementTemplate.TaskAutomations ?? []}
      onChange={next =>
        setCurrentRequirementTemplate({
          ...currentRequirementTemplate,
          TaskAutomations: next,
        })
      }
      legalRequirementStates={legalRequirementStateArray}
    />
  );

  const wissenContent = (
    <KnowledgeItemManager
      items={currentRequirementTemplate.KnowledgeItems ?? []}
      onChange={next =>
        setCurrentRequirementTemplate({
          ...currentRequirementTemplate,
          KnowledgeItems: next,
        })
      }
    />
  );

  return (
    <Box sx={{ m: 2 }}>
      <SectionNavLayout
        header={headerContent}
        sections={[
          {
            key: 'allgemein',
            label: 'Allgemein',
            icon: <ListAltIcon fontSize='small' />,
            content: allgemeinContent,
          },
          {
            key: 'startbedingungen',
            label: 'Startbedingungen',
            icon: <PlaylistAddCheckIcon fontSize='small' />,
            content: startbedingungenContent,
          },
          {
            key: 'email',
            label: 'E-Mail',
            icon: <EmailIcon fontSize='small' />,
            content: emailContent,
          },
          {
            key: 'zustaendigkeit',
            label: 'Bearbeitung & Zuständigkeit',
            icon: <AssignmentIndIcon fontSize='small' />,
            content: zustaendigkeitContent,
          },
          {
            key: 'bereitgestellt',
            label: 'Bereitgestellte Daten',
            icon: <DescriptionIcon fontSize='small' />,
            content: bereitgestellteDatenContent,
          },
          {
            key: 'aufgaben',
            label: 'Aufgaben',
            icon: <ChecklistIcon fontSize='small' />,
            content: aufgabenContent,
          },
          {
            key: 'wissen',
            label: 'Wissensergänzung',
            icon: <PsychologyIcon fontSize='small' />,
            content: wissenContent,
          },
        ]}
      />
      <AddPrerequisiteDeadline
        open={addPrerequisteDeadlineOpen}
        currentLegalRequirementTemplate={currentRequirementTemplate}
        legalRequirementTemplateArray={legalRequirementTemplates}
        setDeadline={handleChangePrerequisteDeadline}
        onClose={() => setAddPrerequisteDeadlineOpen(false)}
        legalPhaseTemplateArray={legalPhaseTemplateArray}
      />
    </Box>
  );
};
